import { useMemo, useState, useRef } from 'react';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Typography,
} from '@mui/material';
import { useDateSort } from '../../../../hooks/useDateSort';
import TableActionsMenu from '../../../../components/common/TableActionsMenu';
import type { Dayjs } from 'dayjs';

/** 컬럼별 티켓 (기존/신규) */
export type RouletteRevenueRowTicket = {
  remainingUserTicket: number;
  newUserTicket: number;
};

export type RouletteRevenueRow = {
  id: number;
  date: string;
  total: number;
  tubeFishing: number;
  scoopFishing: number;
  homeVisit: number;
  shortAd: number;
  adTicket: number;
  videoAd: number;
  repair: number;
  pullup: number;
  fortune: number;
  rcMoreCollect: number;
  attendance: number;
  coupangSeller: number;
  /** 컬럼별 티켓 (전체=합산, 나머지=해당 콘텐츠) */
  ticketTotal?: RouletteRevenueRowTicket;
  ticketTube?: RouletteRevenueRowTicket;
  ticketScoop?: RouletteRevenueRowTicket;
  ticketHomeVisit?: RouletteRevenueRowTicket;
  ticketShortAd?: RouletteRevenueRowTicket;
  ticketAdTicket?: RouletteRevenueRowTicket;
  ticketVideoAd?: RouletteRevenueRowTicket;
  ticketRepair?: RouletteRevenueRowTicket;
  ticketPullup?: RouletteRevenueRowTicket;
  ticketFortune?: RouletteRevenueRowTicket;
  ticketRcMoreCollect?: RouletteRevenueRowTicket;
  ticketAttendance?: RouletteRevenueRowTicket;
};

const HEADERS = [
  '날짜',
  '전체',
  '튜브 낚시',
  '뜰채 낚시',
  '통발 수리',
  '통발 건지기',
  '낚시운세',
  '랜덤 광고',
  '광고보기',
  '영상 광고',
  '홈 방문하기',
  '룰렛 더 모으기',
  '출석체크',
  '쿠팡 셀러',
] as const;

type Props = {
  rows: RouletteRevenueRow[];
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
};

function formatValue(value: number): string {
  if (!Number.isFinite(value)) return '-';
  return value.toLocaleString('ko-KR');
}

function formatCurrency(value: number): string {
  if (!Number.isFinite(value)) return '-';
  return `₩${value.toLocaleString('ko-KR')}`;
}

/** 매출 + (기존/신규 티켓) 두 줄로 표시하는 셀 내용 */
function CellContent({ revenue, ticket }: { revenue: number; ticket?: RouletteRevenueRowTicket }) {
  const hasTicket = ticket && ticket.remainingUserTicket + ticket.newUserTicket > 0;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
      <span>{formatCurrency(revenue)}</span>
      {hasTicket && ticket && (
        <span style={{ color: '#1976d2', fontSize: '0.75rem' }}>
          ({ticket.remainingUserTicket.toLocaleString('ko-KR')} /{' '}
          {ticket.newUserTicket.toLocaleString('ko-KR')})
        </span>
      )}
    </Box>
  );
}

export default function RouletteRevenueTable({ rows, startDate, endDate }: Props) {
  const rowsPerPage = 31;
  const [page, setPage] = useState(0);
  const tableRef = useRef<HTMLTableElement>(null);
  const { sorted, sortDirection, toggleSort } = useDateSort(rows, undefined, 'desc');

  const paged = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page],
  );

  const filename = useMemo(() => {
    if (startDate && endDate) return `${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}`;
    return undefined;
  }, [startDate, endDate]);

  const allRowsDataForExcel = useMemo(
    () =>
      sorted.map((row) => [
        row.date,
        formatValue(row.total),
        formatValue(row.tubeFishing),
        formatValue(row.scoopFishing),
        formatValue(row.repair),
        formatValue(row.pullup),
        formatValue(row.fortune),
        formatValue(row.shortAd),
        formatValue(row.adTicket),
        formatValue(row.videoAd),
        formatValue(row.homeVisit),
        formatValue(row.rcMoreCollect),
        formatValue(row.attendance),
        formatValue(row.coupangSeller),
      ]),
    [sorted],
  );

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: 3,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#313131ff' }}
            />
            <Typography variant="body2" color="text.secondary">
              매출
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#1976d2' }} />
            <Typography variant="body2" sx={{ color: '#1976d2' }}>
              기존 사용자 티켓 발급 수 / 신규 사용자 티켓 발급 수
            </Typography>
          </Stack>
        </Stack>
        <TableActionsMenu
          tableRef={tableRef}
          allRowsData={allRowsDataForExcel}
          filename={filename}
          sheetName="일별 수익"
        />
      </Box>
      <TableContainer sx={{ width: '100%' }}>
        <Table ref={tableRef} stickyHeader sx={{ borderCollapse: 'collapse', width: '100%' }}>
          <TableHead>
            <TableRow sx={{ height: 36 }}>
              <TableCell
                align="center"
                sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                  날짜
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                전체
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                튜브 낚시
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                뜰채 낚시
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                통발 수리
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                통발 건지기
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                낚시운세
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                랜덤 광고
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                광고보기
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                영상 광고
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                홈 방문하기
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                룰렛 더 모으기
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                출석체크
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 'bold', px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                쿠팡 셀러
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={HEADERS.length}
                  align="center"
                  sx={{ py: 3, color: 'text.secondary' }}
                >
                  데이터가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              paged.map((row) => (
                <TableRow key={row.id}>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                      px: '15px',
                      fontSize: '0.8rem',
                    }}
                  >
                    {row.date}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  >
                    <CellContent revenue={row.total} ticket={row.ticketTotal} />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  >
                    <CellContent revenue={row.tubeFishing} ticket={row.ticketTube} />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  >
                    <CellContent revenue={row.scoopFishing} ticket={row.ticketScoop} />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  >
                    <CellContent revenue={row.repair} ticket={row.ticketRepair} />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  >
                    <CellContent revenue={row.pullup} ticket={row.ticketPullup} />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  >
                    <CellContent revenue={row.fortune} ticket={row.ticketFortune} />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  >
                    <CellContent revenue={row.shortAd} ticket={row.ticketShortAd} />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  >
                    <CellContent revenue={row.adTicket} ticket={row.ticketAdTicket} />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  >
                    <CellContent revenue={row.videoAd} ticket={row.ticketVideoAd} />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  >
                    <CellContent revenue={row.homeVisit} ticket={row.ticketHomeVisit} />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  >
                    <CellContent revenue={row.rcMoreCollect} ticket={row.ticketRcMoreCollect} />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  >
                    <CellContent revenue={row.attendance} ticket={row.ticketAttendance} />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ px: '15px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  >
                    <CellContent revenue={row.coupangSeller} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={sorted.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
        />
      </TableContainer>
    </Box>
  );
}
